import { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, PencilIcon } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@nesso/ui/primitives/collapsible';

import {
    ContactForLinks,
    formatDateOfBirth,
    type GetClientProfileOutput,
    type GetClientRelationshipsOutput,
    ProfileDetails,
    type SelectedClient,
} from '@/entities/client';
import { AssignAcuityTag } from '@/features/client/assign-acuity';
import { openEditClientProfileSheet } from '@/features/client/edit-client-profile';
import { ReachContactValue } from '@/features/client/reach-contact';
import { SheetHeader, SheetTitle } from '@/shared/ui/panels/sheet-panel';
import { DotSeparatedText } from '@/shared/ui/text/dot-separated-text';

type Props = {
    client: SelectedClient | undefined;
    name: string | undefined;
    profile: GetClientProfileOutput | undefined;
    contactFor: GetClientRelationshipsOutput['items'];
};

export function ClientDetailsProfileSummary({ client, name, profile, contactFor }: Props) {
    const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);
    const email = profile?.okToEmail === true ? profile.emailPrimary?.trim() : undefined;
    const phone = profile?.okToCallText === true ? profile.phonePrimary?.trim() : undefined;
    const MoreInfoChevron = isMoreInfoOpen ? ChevronUpIcon : ChevronDownIcon;

    return (
        <SheetHeader variant="chart">
            <div className="flex flex-col">
                <SheetTitle className="break-words">{name ?? ''}</SheetTitle>
                <DotSeparatedText
                    orientation="responsive"
                    items={[
                        email && <ReachContactValue key="email" kind="email" value={email} />,
                        phone && <ReachContactValue key="phone" kind="phone" value={phone} />,
                        formatDateOfBirth(profile?.dateOfBirth ?? null),
                    ]}
                />
                <ContactForLinks links={contactFor} />
            </div>
            <AssignAcuityTag />
            <Collapsible
                open={isMoreInfoOpen}
                onOpenChange={setIsMoreInfoOpen}
                className="flex flex-col gap-4 pt-4"
            >
                <div className="flex items-center justify-between -mx-2.5">
                    <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" size="sm" disabled={!profile}>
                            More info
                            <MoreInfoChevron className="size-4" />
                        </Button>
                    </CollapsibleTrigger>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!client}
                        onClick={() => client && openEditClientProfileSheet(client)}
                    >
                        <PencilIcon className="size-4" />
                        Edit Profile
                    </Button>
                </div>
                <CollapsibleContent>
                    {profile && <ProfileDetails profile={profile} />}
                </CollapsibleContent>
            </Collapsible>
        </SheetHeader>
    );
}
